import { http } from '@/apis/aipass'
import { env } from '@/libs/env'
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { ReservationForSpace } from './space-search-reservation-slice'

export interface SpaceReservationInput {
  name: string
  nameKana: string
  tel: string
  email: string
  address: string
  checkinDate: string
  checkoutDate: string
}

export interface AssignSpaceInput {
  usageFrom: string // YYYY-MM-DD HH:MM:DD
  usageTo: string // YYYY-MM-DD HH:MM:DD
  spaceId: string
  reservationUserName: string
  pax: number
  answer: string
}

export interface CreateSpaceReservationInput {
  assignSpaces: AssignSpaceInput[]
  paymentMethodType: string
  hotelId: string
  cardToken?: string
  receiptName?: string
  paymentMethods?: { method: 'cash' | 'credit_card' | 'other'; otherName?: string }[]
}
export type AlignmentReservationInput = CreateSpaceReservationInput & {
  reservationId: string
  guestRoomAssignId: string
}
export type NewReservationInput = CreateSpaceReservationInput & {
  newReservation: SpaceReservationInput
}

export const createSpaceReservation = createAsyncThunk(
  'space/createSpaceReservation',
  async (input: AlignmentReservationInput | NewReservationInput, { rejectWithValue }) => {
    try {
      const response = await http({
        method: 'post',
        url: `${env('BASE_API_V3')}/guest/spaces/reservations`,
        data: input,
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const createSpaceReservationWithoutAuth = createAsyncThunk(
  'space/createSpaceReservationWithoutAuth',
  async (input: AlignmentReservationInput, { rejectWithValue }) => {
    try {
      const response = await http({
        method: 'post',
        url: `${env('BASE_API_V3')}/guest/spaces/reservations-without-auth`,
        data: input,
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

const storageKey = 'spaceReservationSlice'

type stateType = {
  reservationInfo: ReservationForSpace
  hasRoomSettingKey: boolean
}
const restoreState = (): stateType | undefined => {
  const storage = sessionStorage.getItem(storageKey)
  return storage ? JSON.parse(storage) : undefined
}
const initialState: stateType = {
  reservationInfo: {} as ReservationForSpace,
  hasRoomSettingKey: false,
}

const spaceReservationSlice = createSlice({
  name: 'spaceReservations',
  initialState: restoreState() || initialState,
  reducers: {
    setReservationInfo: (state, action) => {
      state.reservationInfo = action.payload
      sessionStorage.setItem(storageKey, JSON.stringify(state))
    },
    clearReservationInfo: state => {
      state.reservationInfo = {} as ReservationForSpace
      sessionStorage.removeItem(storageKey)
    },
    setHasRoomSettingKey: (state, action: PayloadAction<boolean>) => {
      state.hasRoomSettingKey = action.payload
      sessionStorage.setItem(storageKey, JSON.stringify(state))
    },
  },
})

export const { clearReservationInfo, setReservationInfo, setHasRoomSettingKey } = spaceReservationSlice.actions
export const spaceReservationReducer = spaceReservationSlice.reducer
